import { Controller } from "@hotwired/stimulus";

/**
 * Student Access Controller
 *
 * Manages the student access grant interface with:
 * - Modal interactions (open/close)
 * - Dynamic form field visibility based on selection type
 * - Async job submission with progress tracking
 * - Real-time progress updates via polling
 */
export default class extends Controller {
  static targets = [
    "modal",
    "progressModal",
    "form",
    "selectionType",
    "studentsSection",
    "classroomsSection",
    "allSchoolSection",
    "progressBar",
    "progressText",
    "successCount",
    "errorCount",
    "completionMessage",
    "completionText",
    "completionIcon",
    "progressFooter",
    "spinner",
  ];

  static values = {
    jobStatusUrl: String,
    locale: String,
  };

  connect() {
    this.pollingInterval = null;
    this.jobId = null;

    // Get locale from document or default to 'fr'
    this.localeValue = document.documentElement.lang || "fr";
  }

  disconnect() {
    this.stopPolling();
  }

  /**
   * Opens the grant access modal
   */
  openModal(event) {
    event.preventDefault();
    this.modalTarget.classList.remove("hidden");
    document.body.style.overflow = "hidden";
  }

  /**
   * Closes the grant access modal
   */
  closeModal(event) {
    if (event) event.preventDefault();
    this.modalTarget.classList.add("hidden");
    document.body.style.overflow = "auto";
    this.resetForm();
  }

  /**
   * Closes the progress modal and reloads the page
   */
  closeProgressModal(event) {
    if (event) event.preventDefault();
    this.progressModalTarget.classList.add("hidden");
    document.body.style.overflow = "auto";
    // Reload the page to show updated student list
    window.location.reload();
  }

  /**
   * Handles selection type change (students/classrooms/all_school)
   */
  handleSelectionTypeChange(event) {
    const selectedType = event.target.value;

    // Hide all sections first
    this.studentsSectionTarget.classList.add("hidden");
    this.classroomsSectionTarget.classList.add("hidden");
    this.allSchoolSectionTarget.classList.add("hidden");

    // Show relevant section
    switch (selectedType) {
      case "students":
        this.studentsSectionTarget.classList.remove("hidden");
        break;
      case "classrooms":
        this.classroomsSectionTarget.classList.remove("hidden");
        break;
      case "all_school":
        this.allSchoolSectionTarget.classList.remove("hidden");
        break;
    }
  }

  /**
   * Handles form submission - sends request and starts polling
   */
  async submitForm(event) {
    event.preventDefault();

    const form = event.target;
    const formData = new FormData(form);

    try {
      // Send the form as JSON
      const response = await fetch(form.action, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.getCSRFToken(),
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.jobId = data.job_id;

      // Close grant modal and open progress modal
      this.closeModal();
      this.openProgressModal();

      // Start polling for job status
      this.startPolling();
    } catch (error) {
      console.error("Error submitting form:", error);
      alert(
        "Une erreur est survenue lors de la soumission du formulaire. Veuillez réessayer.",
      );
    }
  }

  /**
   * Opens the progress modal
   */
  openProgressModal() {
    this.progressModalTarget.classList.remove("hidden");
    this.resetProgressModal();
    document.body.style.overflow = "hidden";
  }

  /**
   * Resets the progress modal to initial state
   */
  resetProgressModal() {
    this.progressBarTarget.style.width = "0%";
    this.progressBarTarget.setAttribute("aria-valuenow", "0");
    this.progressTextTarget.textContent = this.t("processing");
    this.successCountTarget.textContent = "0";
    this.errorCountTarget.textContent = "0";
    this.completionMessageTarget.classList.add("hidden");
    this.progressFooterTarget.classList.add("hidden");

    // Show spinner at start
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden");
    }
  }

  /**
   * Starts polling for job status
   */
  startPolling() {
    // Poll every 1 second
    this.pollingInterval = setInterval(() => {
      this.checkJobStatus();
    }, 1000);

    // Check immediately
    this.checkJobStatus();
  }

  /**
   * Stops polling
   */
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Checks job status via API
   */
  async checkJobStatus() {
    if (!this.jobId) return;

    try {
      const url = `/${this.localeValue}/schools/settings/student_access/job_status/${this.jobId}`;
      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.getCSRFToken(),
        },
      });

      if (!response.ok) {
        console.error("Error checking job status:", response.status);
        return;
      }

      const data = await response.json();
      this.updateProgress(data);

      // Stop polling if job is complete or failed
      if (data.status === "completed" || data.status === "failed") {
        this.stopPolling();
        this.showCompletion(data);
      }
    } catch (error) {
      console.error("Error checking job status:", error);
    }
  }

  /**
   * Updates progress UI with job data
   */
  updateProgress(data) {
    const {
      processed = 0,
      total = 0,
      success_count = 0,
      error_count = 0,
    } = data;

    // Update progress bar
    const percentage = total > 0 ? Math.round((processed / total) * 100) : 0;
    this.progressBarTarget.style.width = `${percentage}%`;
    this.progressBarTarget.setAttribute("aria-valuenow", percentage);

    // Update text
    this.progressTextTarget.textContent = this.t("students_processed", {
      processed,
      total,
    });
    this.successCountTarget.textContent = success_count;
    this.errorCountTarget.textContent = error_count;
  }

  /**
   * Shows completion message
   */
  showCompletion(data) {
    const { status, success_count = 0, error_count = 0 } = data;

    // Hide the spinner when job is complete
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden");
    }

    this.completionMessageTarget.classList.remove("hidden");
    this.progressFooterTarget.classList.remove("hidden");

    if (status === "completed") {
      // Success state
      this.completionMessageTarget.classList.add("bg-cta-shared");
      this.completionMessageTarget.classList.remove("bg-red-50");
      this.completionIconTarget.classList.add("text-cta");
      this.completionIconTarget.classList.remove("text-red-400");

      this.completionTextTarget.textContent = this.t("completed_message", {
        success_count,
        error_count,
      });
      this.completionTextTarget.classList.add("text-cta-darker");
      this.completionTextTarget.classList.remove("text-red-800");
    } else if (status === "failed") {
      // Error state
      this.completionMessageTarget.classList.add("bg-red-50");
      this.completionMessageTarget.classList.remove("bg-cta-shared");
      this.completionIconTarget.classList.add("text-red-400");
      this.completionIconTarget.classList.remove("text-cta");

      this.completionTextTarget.textContent = this.t("failed");
      this.completionTextTarget.classList.add("text-red-800");
      this.completionTextTarget.classList.remove("text-cta-darker");
    }
  }

  /**
   * Resets the form
   */
  resetForm() {
    this.formTarget.reset();
    this.studentsSectionTarget.classList.add("hidden");
    this.classroomsSectionTarget.classList.add("hidden");
    this.allSchoolSectionTarget.classList.add("hidden");
  }

  /**
   * Gets CSRF token from meta tag
   */
  getCSRFToken() {
    const element = document.querySelector('meta[name="csrf-token"]');
    return element ? element.content : "";
  }

  /**
   * Translation helper
   */
  t(key, params = {}) {
    const translations = {
      fr: {
        processing: "Traitement en cours...",
        students_processed: `${params.processed || 0} / ${params.total || 0} élèves traités`,
        completed_message: `Traitement terminé ! ${params.success_count || 0} accès créés, ${params.error_count || 0} erreurs`,
        failed: "Le traitement a échoué. Veuillez réessayer.",
      },
      en: {
        processing: "Processing...",
        students_processed: `${params.processed || 0} / ${params.total || 0} students processed`,
        completed_message: `Processing completed! ${params.success_count || 0} access granted, ${params.error_count || 0} errors`,
        failed: "Processing failed. Please try again.",
      },
    };

    const locale = this.localeValue || "fr";
    return translations[locale]?.[key] || key;
  }
}
